import * as DG from "ui/dialogs"
import React, { ChangeEvent, useState } from "react"

import {
  BillingTypeFilter,
  BookingOrderRequest,
  FilterType,
  MediaTypeFilters,
} from "models/bookingOrder"
import { Checkbox, Chip } from "@material-ui/core"
import { css, cx } from "@emotion/css"
import {
  getVendorProductTupleOption,
  getVendorTupleOption,
  normalizeVendor,
  normalizeVendorProduct,
} from "./utils"
import { queryCache, useMutation } from "react-query"

import { useAuthenticated } from "providers/A12n"
import { http } from "api/http"
import { ApiError } from "api/http/utils"
import AutoCompleteInput from "components/inputs/AutoComplete"
import Button from "ui/buttons/Button"
import { DateInput } from "components/inputs/DatePicker"
import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import { DescriptionItem } from "components/DescriptionList"
import ExpandMoreIcon from "@material-ui/icons/ExpandMore"
import IconButton from "ui/buttons/IconButton"
import { ReactComponent as InfoSolid } from "assets/icon-infosolid.svg"
import { MediaPlan } from "models/mediaPlan"
import { MorePopover } from "components/MorePopover"
import { MultiSelect } from "routes/Base/Admin/components/MultiSelect"
import Options from "components/inputs/Options"
import { Paginated } from "api/types"
import TextField from "@material-ui/core/TextField"
import { createBookingOrder } from "api/bookingOrder"
import { debounce } from "lodash/fp"
import { normalizeBillingType } from "routes/Base/Plans/Details/cells/BillingTypeCell"
import set from "lodash/fp/set"
import { useHistory } from "react-router"
import { useI18n } from "providers/I18n"
import { useMPFilters } from "routes/Base/BookingOrders/List/utils"
import { useSendFeedBack } from "components/FeedbackSnackbar"

const TUPLE_ID_SEPARATOR = "/"

const groups = [
  "VENDOR",
  "CHANNEL",
  "MEDIA_TYPE",
  "DURATION",
  "LOCATION",
  "BILLING_TYPE",
  "BILLING_SUB_TYPE",
  "PACKAGE",
  "MONTH",
]

const months = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
]

const groupsForLatamBuyer = [
  "MONTH",
  "CHANNEL",
  "BILLING_TYPE",
  "BILLING_SUB_TYPE",
]

type Props = {
  planId?: string
  planName: string
  campaignName?: string
  clientName?: string
  startDate?: string
  endDate?: string
  open: boolean
  onClose: () => void
  mediaPlans?: Paginated<MediaPlan>
  onInputChange?: (value: string) => void
}

const INITIAL_CRITERIAS = {
  vendors: [],
  channels: [],
  mediaTypes: [],
  durations: [],
  locations: [],
  billingTypes: [],
  packages: [],
}

function CreateBoModal(props: Props) {
  const history = useHistory()
  const { t } = useI18n()
  const {
    planId,
    planName,
    campaignName,
    clientName,
    startDate,
    endDate,
    open,
    onClose,
    mediaPlans,
    onInputChange,
  } = props

  const [dates, setDates] = React.useState<Record<string, string | undefined>>()
  const [openGroups, setOpenGroups] = React.useState<boolean>(false)
  const [checked, setChecked] = React.useState<boolean>(false)
  const [isLatamBuyer, setIsLatamBuyer] = React.useState<boolean>(false)
  const [layerIdValue, setLayerIdValue] =
    React.useState<Record<string, unknown>>()

  const [mediaPlan, setMediaPlan] = useState<
    { label: string; value: string } | null | undefined
  >()

  const [criteria, setCriteria] = useState<
    Record<string, { name: string; value: string }[]>
  >(() => INITIAL_CRITERIAS)

  const [selectedCriterias, setSelectedCriterias] = useState<
    Record<string, string>[]
  >([{ label: "vendors", value: "vendors" }])

  const [selectedGroups, setSelectedGroups] = useState<
    { name: string; value: string }[]
  >([])

  const [openMulti, setOpenMulti] = React.useState<Record<string, boolean>>(
    () => ({ vendors: false }),
  )

  const [planItems, setPlanItems] = React.useState<any>([])

  const [monthsToDisplay, setMonthsToDisplay] = React.useState<
    (string | (string | boolean)[])[]
  >([])

  const [selectedMonths, setSelectedMonths] = React.useState<string[]>([])

  const [errors, setErrors] = useState<Record<string, string | undefined>>()

  const sendFeedback = useSendFeedBack()

  const { meta } = useAuthenticated()

  const { mpFilters: filters, hasNoOptions } = useMPFilters(
    (!!planId || !!mediaPlan?.value) && open,
    planId || mediaPlan?.value,
  )

  const [submitBo, { isLoading }] = useMutation(createBookingOrder, {
    onSuccess: () => {
      queryCache.invalidateQueries("booking-orders")
      queryCache.invalidateQueries(["plan", planId])
      queryCache.invalidateQueries(["mediaPlan-filters", planId])
      history.push(
        `/plans/${planId || mediaPlan?.value}/details/booking-orders`,
      )
      setErrors(undefined)
      onModalClose()
    },
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      error.message && sendFeedback(error.message, "error")
      setErrors(errorMap)
    },
  })

  const {
    vendors,
    channels,
    mediaTypes,
    durations,
    locations,
    billingTypes,
    packages,
  } = filters ?? {}

  const selectedMediaPlan = mediaPlans?.data?.find(
    (p) => p?.id === mediaPlan?.value,
  )

  const plansMapped =
    mediaPlans?.data?.map((p) => ({
      value: p?.id,
      label: p?.name,
    })) || []

  const criteriasMapped = Object.keys(filters || {})?.map((c) => ({
    value: c,
    label: t(`create.bo.modal.${c}`),
    disabled: selectedCriterias?.map((sc) => sc?.value).includes(c),
  }))

  const groupsMapped = isLatamBuyer
    ? groupsForLatamBuyer.map((g) => ({
        value: g,
        name: t(`create.bo.modal.filter.${g}`),
      }))
    : groups.map((g) => ({
        value: g,
        name: t(`create.bo.modal.filter.${g}`),
      }))

  const onMediaPlanChange = (
    value: {
      value: string
      label: string
    } | null,
  ) => {
    setMediaPlan(value)
  }

  const onCriteriaOpen = (option: string) => () =>
    setOpenMulti((o) => ({ ...o, [option]: true }))

  const onCriteriaClose = (option: string) => () =>
    setOpenMulti((o) => ({ ...o, [option]: false }))

  const onCriteriaAdd = () => setSelectedCriterias((c) => [...c, {}])

  const onCriteriaChange = (
    evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = evt?.target
    !!name && setSelectedCriterias((c) => set(name, { label: value, value }, c))
  }

  const onCriteriaRemove = (idx: number) => {
    const selected = selectedCriterias[idx].value
    setCriteria((c) => ({ ...c, [selected]: [] }))
    setSelectedCriterias((cr) => {
      let updatedCriterias = [...cr]
      updatedCriterias?.splice(idx, 1)
      return updatedCriterias
    })
  }

  const onToggleOptions = (name: string) => (selectedOptions: Array<any>) =>
    setCriteria((c) => ({ ...c, [name]: selectedOptions }))

  const onBlurOptions = (option?: string) => () =>
    !!option && setOpenMulti((o) => ({ ...o, [option]: false }))

  const onDateChange = (date: Date | null, name: string) =>
    setDates((d) => ({ ...d, [name]: date?.toISOString() }))

  const onCheckBoxChange = () => setChecked(!checked)

  const onToggleGroups = (selectedOptions: Array<any>) =>
    setSelectedGroups(selectedOptions)

  const onGroupsOpen = () => checked && setOpenGroups(true)
  const onGroupsClose = () => setOpenGroups(false)

  const onChipDelete = (group: { name: string; value: string }) =>
    setSelectedGroups((sg) => sg.filter((g) => g.value !== group.value))

  const onModalClose = () => {
    setMediaPlan(undefined)
    setCriteria(() => INITIAL_CRITERIAS)
    setSelectedMonths([])
    if (planId === undefined) {
      setMonthsToDisplay([])
    }
    if (isLatamBuyer) {
      setSelectedCriterias(() => [{ name: "mediaTypes", value: "mediaTypes" }])
      setSelectedGroups([
        { value: "MONTH", name: "Per Month" },
        { value: "CHANNEL", name: "Per Vendor Product" },
        { value: "BILLING_TYPE", name: "Per Billing Type" },
        { value: "BILLING_SUB_TYPE", name: "Per Sub-Billing Type" },
      ])
      setChecked(true)
    } else {
      setSelectedCriterias(() => [{ name: "vendors", value: "vendors" }])
      setSelectedGroups([])
      setChecked(false)
    }
    onClose()
  }

  const onCreateBo = () => {
    const criteriasToSubmitMapped = Object.assign(
      {},
      ...Object.keys(criteria)?.map((c) => {
        if (c !== "mediaTypes" && c !== "packages") {
          return {
            [c]: optionsToSubmit(c as keyof MediaTypeFilters) as Array<
              Record<string, any>
            >,
          }
        } else if (c === "packages") {
          return {
            packageIds: optionsToSubmit("packages") as Array<
              Record<string, any>
            >,
          }
        } else {
          return {
            mediaTypeIds: optionsToSubmit("mediaTypes") as Array<
              Record<string, any>
            >,
          }
        }
      }),
    )
    const groupsToSubmit = selectedGroups?.map((g) => g?.value)
    const boToSubmit = {
      mediaPlanId: planId || mediaPlan?.value,
      criteria: isLatamBuyer
        ? { ...criteriasToSubmitMapped, months: selectedMonths }
        : { ...criteriasToSubmitMapped, ...dates },
      splitBy: groupsToSubmit,
    } as BookingOrderRequest
    submitBo(boToSubmit)
  }

  const optionsMapped = (options: Array<FilterType | string>) =>
    options?.map((o) => ({
      name: typeof o === "string" ? o : o?.name,
      value: typeof o === "string" ? o : o?.id,
    }))

  const criteriaOptions = (name: keyof MediaTypeFilters) => {
    switch (name) {
      case "vendors":
        return vendors?.map((v) => getVendorTupleOption(v)) || []
      case "channels":
        return channels?.map((c) => getVendorProductTupleOption(c)) || []
      case "mediaTypes":
        return optionsMapped(mediaTypes || [])
      case "locations":
        return optionsMapped(locations || [])
      case "durations":
        return optionsMapped(durations?.map((d) => d?.toString()) || [])
      case "billingTypes":
        return getBillingTypeOptions(billingTypes)
      case "packages":
        return optionsMapped(
          packages?.map((p) => ({ id: p?.id, name: p?.name })) || [],
        )
      default:
        return []
    }
  }

  const optionsToSubmit = (name: keyof MediaTypeFilters) => {
    switch (name) {
      case "vendors":
        return criteria?.vendors?.map((v) => normalizeVendor(v?.value)) || []
      case "channels":
        return (
          criteria?.channels?.map((c) => normalizeVendorProduct(c?.value)) || []
        )
      case "mediaTypes":
        return criteria?.mediaTypes?.map((mt) => mt?.value) || []
      case "locations":
        return criteria?.locations?.map((l) => l?.value) || []
      case "durations":
        return criteria?.durations?.map((d) => Number(d.value)) || []
      case "billingTypes":
        return (
          criteria?.billingTypes?.map((bt) => normalizeBillingType(bt.value)) ||
          []
        )
      case "packages":
        return criteria?.packages?.map((st) => st?.value) || []
      default:
        return []
    }
  }

  const groupInputText = selectedGroups?.length
    ? `${selectedGroups?.length} ${t("create.bo.modal.split.selected")}`
    : ""

  const criteriasInputText = (option: string) =>
    criteria[option]
      ?.slice(0, 2)
      ?.map((c) => c?.name)
      .join(", ") || t(!!option ? `create.bo.modal.all.${option}` : "")

  const allIntermediaryVendors =
    filters?.vendors?.map((iv) => iv?.intermediary?.id || "") || []

  const selectedIntermediaryVendors =
    criteria?.vendors?.map(
      (siv) => normalizeVendor(siv?.value || "")?.intermediaryId || "",
    ) || []

  const intermediaryVendors = !!criteria?.vendors?.length
    ? selectedIntermediaryVendors
    : allIntermediaryVendors

  const intercompanyVendors = filters?.vendors?.filter(
    (v) => v?.intermediary?.type === "INTERCOMPANY",
  )

  const filteredIntercompanyVendors =
    intercompanyVendors?.filter((iv) =>
      intermediaryVendors?.includes(iv?.intermediary?.id || ""),
    ) || []

  const intercompanyVendorsText = () => {
    const joinText =
      filteredIntercompanyVendors?.length > 1
        ? t("vendors.bo.info.plural")
        : t("vendors.bo.info.singular")
    return `${intercompanyVendors
      ?.map((iv) => getVendorTupleOption(iv)?.name)
      ?.join(", ")} ${joinText} ${t("vendors.bo.info")}`
  }

  const onMonthClick = (month: string) => {
    const selectedMths = selectedMonths.map((x) => x)

    const mth = month
      ?.split("/")[0]
      .concat("/")
      .concat(
        months[
          months.findIndex((m) => month?.split("/")[1] === m)
        ].toLowerCase(),
      )

    selectedMths.includes(mth)
      ? selectedMths.splice(
          selectedMths.findIndex((m) => m === mth),
          1,
        )
      : selectedMths.push(mth)

    setSelectedMonths(selectedMths)
  }

  const getLayerIdValue = async (id: string | undefined) => {
    const res: Record<string, unknown> = await http.get(`markets/${id}`).json()
    setLayerIdValue(res)
  }

  const fetchMediaPlanSpots = async (planId: string | undefined) => {
    const res: Record<string, unknown> = await http
      .get(`media-plans/${planId}`)
      .json()
    setPlanItems(res.flowPlanItems ?? [])
  }

  function getMonthsInRange(startTxt: string, endTxt: string) {
    // ['2024-12-15', '2025-04-15']
    const startDate = new Date(startTxt)
    let endDate = new Date(endTxt)
    // ensure that endDate will be set later than start date, because is the month what matters here
    endDate.setDate(endDate.getDate() + 2)
    let yearsAndMonths: string[] = []
    let currentDate = new Date(startDate)

    while (currentDate <= endDate) {
      const month = currentDate.getMonth()
      const year = currentDate.getFullYear()
      const yearMonth = year
        .toString()
        .concat("/")
        .concat(months[month].toLowerCase())
      if (!yearsAndMonths.includes(yearMonth)) {
        yearsAndMonths.push(yearMonth)
      }
      currentDate.setMonth(currentDate.getMonth() + 1)
    }

    return yearsAndMonths
  }

  const getDatesFromSpots = (pItems: Array<any>) => {
    let spotsDatePairs: Array<any> = []
    let uniqueYearsMonths: Array<any> = []
    let entireRange: Array<any> = []
    let monthsToDisplay: Array<any> = []

    // Get startDate and endDate from each Plan item, as an array of pairs (spotsDatePairs)
    const pushNewItem = (index: number, item: string) => {
      spotsDatePairs[index].push(item)
    }

    for (let i = 0; i < pItems.length; i++) {
      spotsDatePairs.push([])

      Object.keys(pItems[i]).forEach((key) => {
        const pItemProp = pItems[i][key]
        if (
          typeof pItemProp === "string" &&
          /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z/.test(pItemProp)
        )
          pushNewItem(i, pItemProp.split("T")[0].slice(0, -2).concat("15"))
      })
    }

    // Get all months contained within startDate - endDate range, verifying dates for each Plan item

    spotsDatePairs.forEach((pair) => {
      const startTxt =
        new Date(pair[0]) <= new Date(pair[1]) ? pair[0] : pair[1]
      const endTxt = new Date(pair[0]) > new Date(pair[1]) ? pair[0] : pair[1]
      const range = getMonthsInRange(startTxt, endTxt)
      range.forEach((ym) => {
        if (!uniqueYearsMonths.includes(ym)) {
          uniqueYearsMonths.push(ym)
        }
      })
    })

    // Organize array of unique year/month strings found, by date, from earliest to latest
    const uniqueDates = uniqueYearsMonths.map(
      (ym) =>
        new Date(
          ym
            .split("/")[0]
            .concat("-")
            .concat(
              (months.indexOf(ym.split("/")[1].toUpperCase()) + 1).toString(),
            )
            .concat("-15"),
        ),
    )
    uniqueDates.sort((a: any, b: any) => a - b)
    uniqueYearsMonths = uniqueDates.map((date) =>
      date
        .getFullYear()
        .toString()
        .concat("/")
        .concat(months[date.getMonth()].toLowerCase()),
    )

    // Get the year/month values contained within entire range of dates of the media plan,
    // but have not an active Spot on them, so have to be displayed as disabled buttons
    const startTxt = uniqueYearsMonths[0]
      .split("/")[0]
      .concat("-")
      .concat(
        (
          months.indexOf(uniqueYearsMonths[0].split("/")[1].toUpperCase()) + 1
        ).toString(),
      )
      .concat("-15")
    const endTxt = uniqueYearsMonths[uniqueYearsMonths.length - 1]
      .split("/")[0]
      .concat("-")
      .concat(
        (
          months.indexOf(
            uniqueYearsMonths[uniqueYearsMonths.length - 1]
              .split("/")[1]
              .toUpperCase(),
          ) + 1
        ).toString(),
      )
      .concat("-15")
    entireRange = getMonthsInRange(startTxt, endTxt)

    // Set the arranged year/month strings to defined format to be displayed, as buttons enabled for user to click on them,
    // or grayed out ones
    monthsToDisplay = entireRange.map((ym) => [
      ym.toUpperCase(),
      uniqueYearsMonths.includes(ym) ? "enabled" : "disabled",
    ])
    setMonthsToDisplay(monthsToDisplay)
  }

  React.useEffect(() => {
    if (planItems.length > 0) {
      getDatesFromSpots(planItems.map((li: any) => li.planningMediaFields))
    }
    // eslint-disable-next-line
  }, [planItems])

  React.useEffect(() => {
    setDates({
      startDate: startDate || selectedMediaPlan?.start,
      endDate: endDate || selectedMediaPlan?.end,
    })
  }, [startDate, endDate, selectedMediaPlan, open, onInputChange])

  React.useEffect(() => {
    if (selectedMediaPlan !== undefined) {
      fetchMediaPlanSpots(selectedMediaPlan.id)
    }
  }, [selectedMediaPlan])

  React.useEffect(() => {
    if (planId !== undefined && mediaPlan === undefined) {
      fetchMediaPlanSpots(planId)
    }
  }, [planId, mediaPlan])

  React.useEffect(() => {
    if (mediaPlan !== undefined && mediaPlan !== null) {
      fetchMediaPlanSpots(mediaPlan?.value)
    }
    if (mediaPlan === undefined || mediaPlan === null) {
      setMonthsToDisplay([])
      setSelectedMonths([])
    }
  }, [mediaPlan])

  React.useEffect(() => {
    if (meta?.userRole === "BUYER") {
      getLayerIdValue(meta?.layerId)
    }
  }, [meta])

  React.useEffect(() => {
    // setIsLatamBuyer(layerIdValue?.code === "CHL" ? true : false)
    setIsLatamBuyer(false)
  }, [layerIdValue])

  React.useEffect(() => {
    if (isLatamBuyer) {
      setSelectedGroups([
        { value: "MONTH", name: "Per Month" },
        { value: "CHANNEL", name: "Per Vendor Product" },
        { value: "BILLING_TYPE", name: "Per Billing Type" },
        { value: "BILLING_SUB_TYPE", name: "Per Sub-Billing Type" },
      ])
      setSelectedCriterias(() => [{ name: "mediaTypes", value: "mediaTypes" }])
    }
  }, [isLatamBuyer])

  const id = "create-bo-modal"

  return (
    <DG.Dialog open={open} onClose={onModalClose} id={id}>
      <DG.DialogHeader>
        <DG.DialogTitle id={id}>{t("create.bo.modal.title")}</DG.DialogTitle>
        <DG.DialogClose onClose={onModalClose} disabled={isLoading} />
      </DG.DialogHeader>

      <DG.DialogContent>
        <div className={styles.descriptionContainer}>
          <div className="BodyBody-1BlackLight">
            {t("create.bo.modal.instructions")}
          </div>
          <div className={styles.description}>
            <DescriptionItem
              label={t("create.bo.modal.client")}
              value={clientName || selectedMediaPlan?.clientName}
              keyClassName="BodyBody-sBlackLight"
              valueClassName={cx(
                "BodyBody-sBlackRegular",
                styles.descriptionItem,
              )}
            />
            <div className={styles.separator} />
            <DescriptionItem
              label={t("create.bo.modal.campaign")}
              value={campaignName || selectedMediaPlan?.campaignName}
              keyClassName="BodyBody-sBlackLight"
              valueClassName={cx(
                "BodyBody-sBlackRegular",
                styles.descriptionItem,
              )}
            />
            <div className={styles.separator} />
            <DescriptionItem
              label={t("create.bo.modal.mediaPlan")}
              value={planName || selectedMediaPlan?.name}
              keyClassName="BodyBody-sBlackLight"
              valueClassName={cx(
                "BodyBody-sBlackRegular",
                styles.descriptionItem,
              )}
            />
          </div>
        </div>
        {!planId ? (
          <>
            <h5 className="BodyBody-1BlackBold">
              {t("create.bo.modal.mediaPlan")}
            </h5>
            <div className={styles.filtersContainer}>
              <div className={styles.mediaPlanInput}>
                <AutoCompleteInput<{
                  value: string
                  label: string
                } | null>
                  id="mediaPlanId"
                  name="mediaPlanId"
                  label={t("create.bo.modal.mediaPlan")}
                  autoComplete="off"
                  onInputChange={
                    !!onInputChange ? debounce(300, onInputChange) : undefined
                  }
                  value={mediaPlan}
                  options={plansMapped}
                  renderOption={(option) => <span>{option?.label}</span>}
                  onChange={onMediaPlanChange}
                  getOptionLabel={(option) => option?.label || ""}
                  getOptionSelected={(opt, val) => opt === val}
                  errorMessage={!!errors ? errors["mediaPlanId"] : ""}
                  className={styles.input}
                />
              </div>
            </div>
          </>
        ) : null}

        <h5 className="BodyBody-1BlackBold">
          {t("create.bo.modal.filter.by")}
        </h5>
        <div className={styles.filtersContainer}>
          <div className={styles.filtersTitleRow}>
            <h6 className={cx("BodyBody-2Dark-greyRegular")}>
              {t("create.bo.modal.criteria")}
            </h6>
            <h6 className={cx("BodyBody-2Dark-greyRegular")}>
              {t("create.bo.modal.selection")}
            </h6>
          </div>
          <div className={styles.optionsGrid}>
            {selectedCriterias?.map((k, kidx) => (
              <div className={styles.optionsLine} key={kidx}>
                <div
                  key={kidx}
                  className={cx(styles.options, {
                    [styles.firstOption]:
                      !!intercompanyVendors?.length && k?.value === "vendors",
                  })}
                >
                  {k?.value === "vendors" ? (
                    <TextField
                      variant="outlined"
                      color="primary"
                      fullWidth
                      value={t(`create.bo.modal.${k?.value}`)}
                      className={styles.optionName}
                    />
                  ) : (
                    <Options
                      name={`${kidx}`}
                      options={criteriasMapped}
                      value={k?.value}
                      size="medium"
                      onChange={onCriteriaChange}
                      fullWidth
                    />
                  )}
                  <MultiSelect
                    items={criteriaOptions(k?.value as keyof MediaTypeFilters)}
                    getOptionLabel={(option: Record<string, string>) =>
                      option?.name
                    }
                    selectedValues={criteria[k?.value] || []}
                    checkValues={() => true}
                    renderOption={(option: Record<string, string>) =>
                      option?.name
                    }
                    onToggleOption={onToggleOptions(k?.value)}
                    onBlur={onBlurOptions(k?.value)}
                    className={styles.criteriasMulti}
                    paperClassName={styles.multiselectList}
                    open={!!openMulti[k?.value]}
                    onOpen={onCriteriaOpen(k?.value)}
                    onClose={onCriteriaClose(k?.value)}
                    inputValue={criteriasInputText(k?.value) || ""}
                    hasMoreLabel={k?.value}
                    endAdornment={
                      <MorePopover
                        data={criteria[k?.value]?.map((sv: any) => ({
                          name: sv?.name,
                          value: sv?.value,
                        }))}
                        label={`${k?.value}.bo`}
                        name="name"
                        slice={2}
                      />
                    }
                  />
                  {!!intercompanyVendors?.length && k?.value === "vendors" ? (
                    <div className={styles.info}>
                      <InfoSolid className={styles.infoIcon} />
                      <span
                        className={cx(
                          "BodyBody-sDark-greyRegular",
                          styles.infoText,
                        )}
                      >
                        {intercompanyVendorsText()}
                      </span>
                    </div>
                  ) : null}
                </div>
                {k?.value !== "vendors" ? (
                  <IconButton
                    className={styles.iconButton}
                    onClick={() => onCriteriaRemove(kidx)}
                  >
                    <DeleteIcon color="inherit" className={styles.deleteIcon} />
                  </IconButton>
                ) : null}
              </div>
            ))}
          </div>
          <Button
            color="primary"
            className={styles.textButton}
            onClick={onCriteriaAdd}
            disabled={selectedCriterias?.length === criteriasMapped?.length}
          >
            {t("create.bo.modal.add.criteria")}
          </Button>
        </div>

        <h5 className="BodyBody-1BlackBold">
          {t("create.bo.modal.bo.ordering.period")}
        </h5>

        {isLatamBuyer ? (
          <div className={styles.monthsGrid}>
            {monthsToDisplay?.map((k, kidx) => (
              <div className={styles.monthsLine} key={kidx}>
                <Button
                  variant="outlined"
                  color="default"
                  disabled={k[1]?.toString() === "disabled" ? true : false}
                  className={
                    selectedMonths.includes(k[0]?.toString().toLowerCase())
                      ? styles.monthNameSelected
                      : styles.monthNameUnselected
                  }
                  onClick={() => onMonthClick(k[0]?.toString())}
                >
                  {k[0]?.toString()}
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.dates}>
            <DateInput
              autoOk
              name="startDate"
              value={dates?.startDate || undefined}
              onChange={onDateChange}
              inputVariant="outlined"
              label={t("create.bo.modal.startDate")}
              fullWidth={false}
              dropdownIcon={true}
              minDate={startDate || selectedMediaPlan?.start}
              // error={Boolean(errors.approvalDate)}
              // helperText={errors.approvalDate}
              hasStartAdornment
            />
            <DateInput
              autoOk
              name="endDate"
              value={dates?.endDate || undefined}
              onChange={onDateChange}
              inputVariant="outlined"
              label={t("create.bo.modal.endDate")}
              fullWidth={false}
              dropdownIcon={true}
              maxDate={endDate || selectedMediaPlan?.end}
              // error={Boolean(errors.approvalDate)}
              // helperText={errors.approvalDate}
              hasStartAdornment
            />
          </div>
        )}

        <h5 className="BodyBody-1BlackBold">
          {t("create.bo.modal.split.preferences")}
        </h5>
        <div className={styles.splitContainer}>
          <div className={styles.splitGroup}>
            <Checkbox
              className={styles.checkbox}
              size="medium"
              color="primary"
              checked={checked || isLatamBuyer}
              disabled={isLatamBuyer}
              onChange={onCheckBoxChange}
            />
            <div className="BodyBody-2BlackRegular">
              {t("create.bo.modal.split.bo")}
            </div>
          </div>
          <MultiSelect
            items={groupsMapped}
            label={t("create.bo.modal.split.bo")}
            getOptionLabel={(option: Record<string, string>) => option?.name}
            selectedValues={selectedGroups || []}
            checkValues={() => true}
            renderOption={(option: Record<string, string>) => option?.name}
            onToggleOption={onToggleGroups}
            className={styles.groupsMulti}
            paperClassName={styles.multiselectList}
            disabled={!checked || isLatamBuyer}
            inputValue={groupInputText}
            endAdornment={<ExpandMoreIcon />}
            open={openGroups}
            onOpen={onGroupsOpen}
            onClose={onGroupsClose}
          />
          <div className={styles.chipsContainer}>
            {selectedGroups.map((group, gidx) => (
              <Chip
                color="primary"
                label={group?.name}
                disabled={isLatamBuyer}
                onDelete={() => onChipDelete(group)}
                key={gidx}
              />
            ))}
          </div>
        </div>
      </DG.DialogContent>

      <DG.DialogFooter>
        <DG.DialogActions>
          <Button
            variant="outlined"
            color="primary"
            onClick={onModalClose}
            disabled={isLoading}
          >
            {t("warning.modal.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onCreateBo}
            disabled={
              isLoading ||
              !(planId || mediaPlan?.value) ||
              hasNoOptions ||
              (isLatamBuyer && selectedMonths?.length === 0)
            }
          >
            {t("create.bo.modal.create")}
          </Button>
        </DG.DialogActions>
      </DG.DialogFooter>
    </DG.Dialog>
  )
}

export default CreateBoModal

function billingTypeOption(bt: BillingTypeFilter) {
  let option: { value: string; name: string } | undefined
  if (!bt.billingSubtypeId)
    option = { value: bt.billingTypeId, name: bt.billingTypeId }

  option = {
    value: [bt.billingTypeId, bt.billingSubtypeId].join(TUPLE_ID_SEPARATOR),
    name: [bt.billingTypeName, bt.billingSubtypeName].join(
      ` ${TUPLE_ID_SEPARATOR} `,
    ),
  }

  return option
}

function getBillingTypeOptions(billingTypes?: BillingTypeFilter[]) {
  const options: Array<{ value: string; name: string }> = []
  billingTypes?.forEach((bt) => {
    const option = billingTypeOption(bt)
    options.push(option)
  })

  return options
}

const styles = {
  descriptionContainer: css`
    padding-bottom: var(--spacing-xxl-4);
  `,
  description: css`
    padding-top: var(--spacing-xxs);
    display: grid;
    grid-auto-flow: column;
    grid-template-columns: max-max-content;
    grid-column-gap: var(--spacing-xxs);
    align-items: center;
    width: fit-content;
  `,
  descriptionItem: css`
    margin-right: 0;
    align-self: flex-end;
  `,
  separator: css`
    width: 1px;
    height: 14px;
    display: inline-block;
    background-color: var(--disabled-grey);
    margin: 0 var(--spacing-xxs) var(--spacing-xxs);
  `,
  filtersContainer: css`
    display: flex;
    flex-direction: column;
    margin: var(--spacing-xl) 0 var(--spacing-xxl-7);
  `,
  mediaPlanInput: css`
    display: flex;
    width: 256px;
  `,
  filtersTitleRow: css`
    display: grid;
    grid-template-columns: repeat(2, 200px);
    grid-column-gap: var(--spacing-m);
    width: 432px;
    padding: var(--spacing-xs);
    padding-bottom: 6px;
  `,
  monthsGrid: css`
    display: grid;
    grid-template-columns: auto auto auto auto auto auto;
    grid-column-gap: var(--spacing-xs);
    grid-row-gap: var(--spacing-xs);
    padding-right: 50px;
    border-radius: 2px;
    margin: var(--spacing-xl) 0 var(--spacing-xxl-7);
  `,
  monthsLine: css`
    display: grid;
    align-items: center;
  `,
  monthNameSelected: css`
    padding: 1px;
    font-weight: normal;
    border-color: var(--green);
    border-width: 2px;
    background-color: var(--white);
  `,
  monthNameUnselected: css`
    padding: 1px;
    font-weight: normal;
    background-color: var(--white);
  `,
  optionsGrid: css`
    display: grid;
    grid-row-gap: var(--spacing-m);
    width: 432px;
  `,
  optionsLine: css`
    display: flex;
    align-items: center;
  `,
  options: css`
    display: grid;
    grid-template-columns: repeat(2, 200px);
    grid-column-gap: var(--spacing-m);
    grid-row-gap: var(--spacing-xxs);
    padding: var(--spacing-xs);
    background-color: var(--light-grey-2);
    border-radius: 2px;
  `,
  firstOption: css`
    padding-bottom: var(--spacing-xxs);
  `,
  criteriasMulti: css`
    background-color: var(--white);
    .MuiAutocomplete-input {
      width: 100%;
    }
    .MuiFormLabel-root {
      color: var(--black);
    }
  `,
  groupsMulti: css`
    width: 180px;
  `,
  optionName: css`
    background-color: var(--white);
  `,
  optionsButton: css`
    background-color: var(--white);
    border: 1px solid var(--light-grey);
    border-radius: 4px;
    > button > span {
      font-size: 14px;
    }
  `,
  info: css`
    grid-column-start: 1;
    grid-column-end: 3;
    display: flex;
    align-items: center;
    padding: 2px;
  `,
  infoIcon: css`
    color: var(--tertiary-light-yellow);
    margin-right: var(--spacing-xxs);
  `,
  infoText: css`
    width: 100%;
  `,
  textButton: css`
    --btn-padding: 0;
    --btn-min-width: 0;
    --btn-line-height: 1;
    --btn-color: inherit;
    --btn-font-weight: normal;
    --btn-bg-hover: transparent;
    --btn-bg-active: transparent;
    --btn-border-color-hover: transparent;
    --btn-bg-focus-ring-color: transparent;
    width: fit-content;
    margin-top: var(--spacing-m);
    &:focus {
      box-shadow: none;
    }
    &[aria-disabled="true"] {
      background-color: transparent;
    }
  `,
  iconButton: css`
    margin-left: var(--spacing-s);
    &.MuiIconButton-root {
      margin-left: var(--spacing-s);
      margin-top: var(--spacing-xs);
    }
  `,
  deleteIcon: css`
    width: 24px;
    height: 24px;
    cursor: pointer;
    > path {
      fill: var(--grey);
    }
  `,
  multiselectList: css`
    > ul {
      > li:first-child {
        color: inherit;
      }
    }
  `,
  dates: css`
    margin: var(--spacing-xl) 0 var(--spacing-xxl-7);
    display: grid;
    grid-template-columns: repeat(2, 180px);
    grid-column-gap: var(--spacing-m);
    align-items: center;
  `,
  splitContainer: css`
    margin: var(--spacing-xl) 0 var(--spacing-xxl-4);
    display: grid;
    grid-template-columns: max-content 180px;
    grid-column-gap: var(--spacing-m);
    grid-row-gap: var(--spacing-m);
  `,
  splitGroup: css`
    display: flex;
    align-items: center;
  `,
  checkbox: css`
    padding: 0;
    padding-right: var(--spacing-xs);
    .MuiSvgIcon-root {
      width: 24px;
      height: 24px;
    }
  `,
  chipsContainer: css`
    grid-column-start: 2;
    grid-column-end: none;
  `,
  input: css`
    min-width: 240px;
  `,
}
